import { useState } from 'react'
import { Bug, Lightbulb, ArrowLeft, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'

const TYPES = {
  bug: { label: 'Report Bug', icon: Bug, colour: 'destructive' },
  'feature-request': { label: 'Feature Request', icon: Lightbulb, colour: 'secondary' },
}

export default function FeedbackForm({ onSubmit, onCancel, isSubmitting }) {
  const [type, setType] = useState(null)
  const [description, setDescription] = useState('')
  const [includeScreenshot, setIncludeScreenshot] = useState(false)
  const [includeConsoleLogs, setIncludeConsoleLogs] = useState(false)

  const handleSubmit = () => {
    if (!description.trim()) return
    onSubmit({ type, description: description.trim(), includeScreenshot, includeConsoleLogs })
  }

  const handleBack = () => {
    setType(null)
  }

  // View 1: Type selection
  if (!type) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          Please choose feedback type:
        </p>
        <div className="flex flex-col gap-2">
          {Object.entries(TYPES).map(([key, { label, icon: Icon }]) => (
            <button
              key={key}
              onClick={() => setType(key)}
              className="flex items-center gap-3 rounded-lg border border-border p-3 text-left text-sm font-medium transition-colors hover:bg-accent hover:border-accent-foreground/20"
            >
              <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
              {label}
            </button>
          ))}
        </div>
      </div>
    )
  }

  // View 2: Description form
  const { label, colour } = TYPES[type]

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <button
          onClick={handleBack}
          className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          disabled={isSubmitting}
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <Badge variant={colour}>{label}</Badge>
      </div>

      <Textarea
        placeholder={
          type === 'bug'
            ? 'Describe what happened and what you expected...'
            : 'Describe the feature you would like...'
        }
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        className="min-h-[120px] resize-none"
        disabled={isSubmitting}
        autoFocus
      />

      <div className="flex flex-col gap-2">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox
            checked={includeScreenshot}
            onCheckedChange={setIncludeScreenshot}
            disabled={isSubmitting}
          />
          Include screenshot
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox
            checked={includeConsoleLogs}
            onCheckedChange={setIncludeConsoleLogs}
            disabled={isSubmitting}
          />
          Include console logs
        </label>
      </div>

      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={!description.trim() || isSubmitting}
        >
          {isSubmitting ? (
            'Submitting...'
          ) : (
            <>
              <Send className="h-3.5 w-3.5 mr-1.5" />
              Submit
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
